import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";
import Icon from "../../Icon";

const ErrorBoundary = () => {
  const [expandedError, setExpandedError] = useState(null);

  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* SECTION 1: ERROR STATES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Error States</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 1: 404 Not Found */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">404 Not Found</label>
            <div className="p-6 bg-slate-950 rounded border border-slate-600 text-center">
              <div className="text-6xl mb-3 flex justify-center"><Icon name="search" className="w-10 h-10" /></div>
              <h3 className="text-lg font-bold text-slate-100 mb-2">404 - Page Not Found</h3>
              <p className="text-sm text-slate-400 mb-4">The page you're looking for doesn't exist.</p>
              <button className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-sm font-semibold text-white rounded">Go Home</button>
            </div>
            <CopyCode number_code={53} code={`<div className="p-6 bg-slate-950 rounded text-center">
  <div className="text-6xl mb-3">Icon</div>
  <h3 className="text-lg font-bold mb-2">404 Not Found</h3>
  <p className="text-slate-400 mb-4">
    The page doesn't exist.
  </p>
  <button className="px-4 py-2 
    bg-amber-600 text-white rounded">
    Go Home
  </button>
</div>`} />
          </div>

          {/* Variation 2: 500 Error */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">500 Server Error</label>
            <div className="p-6 bg-slate-950 rounded border border-red-700/50">
              <div className="text-5xl mb-3 text-center inline-flex justify-center w-full"><Icon name="bolt" className="w-9 h-9" /></div>
              <h3 className="text-lg font-bold text-red-400 mb-2">500 Server Error</h3>
              <p className="text-sm text-slate-400 mb-4">Something went wrong on our end. Our team is investigating.</p>
              <p className="text-xs text-slate-500 mb-3">Error ID: ERR-5029-X7K2</p>
              <button className="w-full px-4 py-2 bg-red-600 hover:bg-red-500 text-sm font-semibold text-white rounded">Report Issue</button>
            </div>
            <CopyCode number_code={54} code={`<div className="p-6 bg-slate-950 rounded 
  border border-red-700/50">
  <div className="text-5xl mb-3 text-center">Icon</div>
  <h3 className="text-lg text-red-400 font-bold">
    500 Server Error
  </h3>
  <p className="text-slate-400 text-sm mb-4">
    Something went wrong on our end.
  </p>
  <p className="text-xs text-slate-500 mb-3">
    Error ID: ERR-5029-X7K2
  </p>
</div>`} />
          </div>

          {/* Variation 3: Network Error */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Network Error</label>
            <div className="p-6 bg-slate-950 rounded border border-orange-700/50">
              <div className="text-5xl mb-3 text-center inline-flex justify-center w-full"><Icon name="wifi" className="w-9 h-9" /></div>
              <h3 className="text-lg font-bold text-orange-400 mb-2">Connection Failed</h3>
              <p className="text-sm text-slate-400 mb-4">Unable to reach the server. Check your internet connection.</p>
              <div className="flex gap-2">
                <button className="flex-1 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-sm font-semibold text-white rounded">Retry</button>
                <button className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-sm font-semibold text-slate-100 rounded">Offline Mode</button>
              </div>
            </div>
            <CopyCode number_code={55} code={`<div className="p-6 bg-slate-950 rounded 
  border border-orange-700/50">
  <div className="text-5xl mb-3">Icon</div>
  <h3 className="text-lg text-orange-400 font-bold">
    Connection Failed
  </h3>
  <p className="text-slate-400 text-sm">
    Check your internet connection.
  </p>
  <div className="flex gap-2 mt-4">
    <button className="flex-1 bg-orange-600 text-white
      rounded">Retry</button>
  </div>
</div>`} />
          </div>

          {/* Variation 4: Validation Error */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Validation Error</label>
            <div className="p-4 bg-slate-950 rounded border border-red-700/50">
              <h3 className="text-sm font-bold text-red-400 mb-3 inline-flex items-center gap-2"><Icon name="close" className="w-4 h-4" /> Form Validation Failed</h3>
              <div className="space-y-2 text-sm">
                <div className="p-2 bg-red-900/20 border border-red-700/30 rounded">
                  <p className="text-red-300">• Email is invalid</p>
                </div>
                <div className="p-2 bg-red-900/20 border border-red-700/30 rounded">
                  <p className="text-red-300">• Password must be 8+ characters</p>
                </div>
                <div className="p-2 bg-red-900/20 border border-red-700/30 rounded">
                  <p className="text-red-300">• Terms must be accepted</p>
                </div>
              </div>
            </div>
            <CopyCode number_code={56} code={`<div className="p-4 rounded">
  <h3 className="text-sm font-bold text-red-400">
    Validation Failed
  </h3>
  <div className="space-y-2 text-sm mt-3">
    <div className="p-2 bg-red-900/20 rounded">
      <p className="text-red-300">• Email is invalid</p>
    </div>
    <div className="p-2 bg-red-900/20 rounded">
      <p className="text-red-300">
        • Password must be 8+ chars
      </p>
    </div>
  </div>
</div>`} />
          </div>

        </div>
      </div>

      {/* SECTION 2: ERROR DETAILS */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Error Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Variation 5: Stack Trace */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Stack Trace</label>
            <div className="p-4 bg-slate-950 rounded border border-red-700/50">
              <h3 className="text-sm font-bold text-red-400 mb-2 inline-flex items-center gap-2"><Icon name="warning" className="w-4 h-4" /> JavaScript Error</h3>
              <p className="text-xs text-slate-400 mb-3">TypeError: Cannot read property 'map' of undefined</p>
              <button onClick={() => setExpandedError(expandedError === 'stack' ? null : 'stack')} className="text-xs text-slate-400 hover:text-slate-200 mb-2 inline-flex items-center gap-1"><Icon name="play" className="w-3 h-3" /> Stack Trace</button>
              {expandedError === 'stack' && (
                <div className="bg-slate-900 p-2 rounded text-xs text-slate-400 font-mono overflow-auto max-h-24">
                  <p>at Array.map (components.js:45:12)</p>
                  <p>at renderItems (app.jsx:128:3)</p>
                  <p>at Object.render (index.js:8:5)</p>
                </div>
              )}
            </div>
            <CopyCode number_code={57} code={`<div className="p-4 bg-slate-950 rounded">
  <h3 className="text-sm font-bold text-red-400">
    JavaScript Error
  </h3>
  <p className="text-xs text-slate-400 mt-2">
    TypeError: Cannot read property 'map'
  </p>
  <button className="text-xs text-slate-400 mt-2">
    Stack Trace
  </button>
</div>`} />
          </div>

          {/* Variation 6: With Retry */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">With Retry Button</label>
            <div className="p-4 bg-slate-950 rounded border border-red-700/50">
              <h3 className="text-sm font-bold text-red-400 mb-2">Database Connection Failed</h3>
              <p className="text-xs text-slate-400 mb-4">Unable to connect to the database. Retrying might help.</p>
              <div className="flex gap-2">
                <button className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-500 text-xs font-semibold text-white rounded">Retry Now</button>
                <button className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-xs font-semibold text-slate-100 rounded">View Logs</button>
              </div>
            </div>
            <CopyCode number_code={58} code={`<div className="p-4 rounded border border-red-700/50">
  <h3 className="text-sm font-bold text-red-400">
    Database Connection Failed
  </h3>
  <p className="text-xs text-slate-400 mt-2">
    Retrying might help.
  </p>
  <div className="flex gap-2 mt-4">
    <button className="flex-1 bg-red-600 text-white
      rounded text-xs">Retry Now</button>
  </div>
</div>`} />
          </div>

          {/* Variation 7: Helpful Message */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Helpful Message</label>
            <div className="p-4 bg-slate-950 rounded border border-amber-700/50">
              <h3 className="text-sm font-bold text-amber-400 mb-2 inline-flex items-center gap-2"><Icon name="lightbulb" className="w-4 h-4" /> Need Help?</h3>
              <p className="text-xs text-slate-400 mb-3">This error often occurs when the API key is invalid.</p>
              <div className="space-y-2">
                <button className="w-full px-3 py-2 bg-slate-700 hover:bg-slate-600 text-xs font-semibold text-slate-100 rounded text-left inline-flex items-center gap-2"><Icon name="book" className="w-4 h-4" /> View Documentation</button>
                <button className="w-full px-3 py-2 bg-slate-700 hover:bg-slate-600 text-xs font-semibold text-slate-100 rounded text-left inline-flex items-center gap-2"><Icon name="link" className="w-4 h-4" /> Check API Status</button>
              </div>
            </div>
            <CopyCode number_code={59} code={`<div className="p-4 rounded">
  <h3 className="text-sm font-bold text-amber-400">
    Need Help?
  </h3>
  <p className="text-xs text-slate-400 mt-2">
    API key might be invalid.
  </p>
  <button className="w-full mt-3 px-3 py-2 
    bg-slate-700 text-slate-100 rounded">
    View Documentation
  </button>
</div>`} />
          </div>

          {/* Variation 8: Contact Support */}
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Contact Support</label>
            <div className="p-4 bg-slate-950 rounded border border-amber-700/50">
              <h3 className="text-sm font-bold text-amber-400 mb-2">Contact Support</h3>
              <p className="text-xs text-slate-400 mb-3">Error reference: #ERR-8392-FX1K</p>
              <p className="text-xs text-slate-400 mb-4">Our support team can help resolve this issue faster.</p>
              <button className="w-full px-3 py-2 bg-amber-600 hover:bg-amber-500 text-xs font-semibold text-white rounded">Open Support Ticket</button>
            </div>
            <CopyCode number_code={60} code={`<div className="p-4 bg-slate-950 rounded 
  border border-amber-700/50">
  <h3 className="text-sm text-amber-400 font-bold">
    Contact Support
  </h3>
  <p className="text-xs text-slate-400 mt-2">
    Error: #ERR-8392-FX1K
  </p>
  <button className="w-full mt-4 px-3 py-2 
    bg-amber-600 text-white rounded text-xs">
    Open Support Ticket
  </button>
</div>`} />
          </div>

        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;


